import { FC } from 'react';

import { SEO } from 'components/seo/SEO';
import { LogoWithText } from 'components/logo/LogoWithText';
import { SwitchAccounts } from 'components/switch-accounts/SwitchAccounts';
import { detectProduct } from 'helpers/product';

import contextEventDecorator from '_assets/images/context-event-decorator.png';
import Location from 'services/Location';
import { Heading } from '@livechat/design-system-react-components';
import { NewAccount } from './new-account/NewAccount';
import { GoToSignIn } from './go-to-sign-in/GoToSignIn';
import { CreateNewAccount } from './create-new-account/CreateNewAccount';
import { AlreadyHaveAnAccountLink } from './already-have-account/AlreadyHaveAnAccountLink';
import { ProductDropdown } from './product-dropdown/ProductDropdown';
import { ProfileDropdown } from './profile-dropdown/ProfileDropdown';

import * as styles from './styles';

const textLogoFull =
  'https://cdn.static-text.com/text-brand-assets/misc/powered-by-text-accounts%403x.png';
const textLogo = 'https://cdn.static-text.com/text-brand-assets/logo/text/text-black.svg';

type Props = {
  showDropdown?: boolean;
  showNewAccountAction?: boolean;
  showGoToSignUp?: boolean;
  showAlreadyHaveAnAccount?: boolean;
  showHeaderLogo?: boolean;
  showGoToSignIn?: boolean;
  onActionClick?: () => void;
  backToLogin?: boolean;
  children: React.ReactNode;
};

export const Layout: FC<Props> = ({
  children,
  showDropdown = false,
  showNewAccountAction = false,
  showGoToSignUp = false,
  showAlreadyHaveAnAccount = false,
  showHeaderLogo = false,
  showGoToSignIn = false,
  onActionClick,
  backToLogin,
}) => {
  const product = detectProduct();
  const clientId = Location.getUrlParam('client_id');
  const route = Location.getPathName();

  const renderActionButton = () => {
    // this will show different action buttons based on prop
    switch (true) {
      case showAlreadyHaveAnAccount:
        return <AlreadyHaveAnAccountLink />;
      case showDropdown:
        return (
          <div css={styles.actionButtonsContainer}>
            <ProductDropdown />
            <ProfileDropdown />
          </div>
        );
      case showNewAccountAction:
        return <CreateNewAccount />;
      case showGoToSignUp:
        return <NewAccount />;
      case backToLogin:
        return <SwitchAccounts label="Back to login" />;
      case showGoToSignIn:
        return <GoToSignIn onClick={onActionClick} />;
      default:
        return null;
    }
  };

  const renderFooter = () => {
    if (clientId === import.meta.env.VITE_APP_DEFAULT_CLIENT_ID && route !== 'select-trial') {
      return (
        <img src={textLogoFull} css={styles.textLogoFull} alt="Text logo with product logos" />
      );
    }

    return (
      <Heading size="2xs">
        powered by <img src={textLogo} css={styles.textLogo} alt="Text logo" />
      </Heading>
    );
  };

  return (
    <>
      <SEO />
      <div css={styles.container}>
        <div css={styles.heading(clientId)}>
          <div css={styles.logoContainer}>
            {showHeaderLogo && <LogoWithText product={product} />}
          </div>

          <div>{renderActionButton()}</div>
        </div>
        <main css={styles.mainContainer(clientId)}>
          {clientId === import.meta.env.VITE_APP_CONTEXT_EVENT_CLIENT_ID && (
            <img src={contextEventDecorator} css={styles.contextEventDecorator} alt="" />
          )}
          {children}
        </main>
        <div css={styles.footer(clientId)}>{renderFooter()}</div>
      </div>
    </>
  );
};
